import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { useToast } from '../contexts/ToastContext';
import { Clock, CheckCircle, XCircle, Award, AlertCircle, AlertTriangle, Loader, BadgeCheck, Info } from 'lucide-react';
import { Checkbox } from '../components/ui/checkbox';
import { getApiErrorMessage } from '../utils/apiError';
import { DR_DATA_ITEMS } from '../constants/dijitalRozetDataItems';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ExamPage() {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const { authHeaders } = useAuth();
  const { lang, t } = useLanguage();
  const toast = useToast();

  const [phase, setPhase] = useState('loading'); // loading, intro, drConsent, taking, result, error
  const [meta, setMeta] = useState(null);
  const [attempt, setAttempt] = useState(null);
  const [answers, setAnswers] = useState({});
  const [timeLeft, setTimeLeft] = useState(0);
  const [result, setResult] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [materialsStatus, setMaterialsStatus] = useState(null);  // Modul 3.6
  const [drAgreed, setDrAgreed] = useState(false);
  const [drSaving, setDrSaving] = useState(false);
  const drConsentTitleRef = useRef(null);
  // Onay/vazgec yollarindan (buton tikla + Esc) startExam()'in yalnizca
  // bir kez tetiklenmesini garantiler — hizli Esc+tik cift in_progress
  // attempt olusturmasin diye.
  const drConsentResolvedRef = useRef(false);

  const tr = (t, e) => lang === 'tr' ? t : e;

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/courses/${courseId}/final-exam`, {
          headers: authHeaders(),
        });
        setMeta(res.data);
        setPhase('intro');
        // Modul 3.6: Zorunlu materyal durumunu cek
        try {
          const mat = await axios.get(`${API}/courses/${courseId}/required-materials-status`, {
            headers: authHeaders(),
          });
          setMaterialsStatus(mat.data);
        } catch (me) {
          console.error('Materials status check failed:', me);
        }
      } catch (e) {
        setError(e.response?.data?.detail || tr('Sınav yüklenemedi', 'Could not load exam'));
        setPhase('error');
      }
    })();
  }, [courseId]); // eslint-disable-line

  const downloadExamCertPdf = async (certId, courseTitle) => {
    try {
      const res = await axios.get(`${API}/certificates/${certId}/pdf`, {
        headers: authHeaders(),
        responseType: 'blob',
      });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      const safeName = (courseTitle || certId).replace(/[^a-zA-Z0-9ğüşıöçĞÜŞİÖÇ\s-]/g, '').trim().replace(/\s+/g, '-');
      a.download = `sertifika-${safeName}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      const status = err?.response?.status;
      let msg = tr('Sertifika indirilemedi.', 'Could not download certificate.');
      if (status === 401 || status === 403) {
        msg = tr('Oturum süresi doldu. Lütfen tekrar giriş yapın.', 'Session expired. Please log in again.');
      } else {
        try {
          const blob = err?.response?.data;
          if (blob instanceof Blob) {
            const text = await blob.text();
            const json = JSON.parse(text);
            msg = (lang === 'tr' ? json?.message_tr : json?.message_en) || json?.detail || msg;
          }
        } catch { /* yoksay */ }
      }
      toast.error(msg);
    }
  };

  const handleSubmit = useCallback(async (timedOut = false) => {
    if (submitting) return;
    setSubmitting(true);
    try {
      const payload = {
        attempt_id: attempt.attempt_id,
        answers: Object.entries(answers).map(([question_id, selected]) => {
          const q = attempt.questions.find(q => q.question_id === question_id);
          const _idx = q ? q.options.indexOf(selected) : -1;
          const selected_index = _idx >= 0 ? _idx : null;
          return { question_id, selected, selected_index };
        }),
      };
      const res = await axios.post(
        `${API}/courses/${courseId}/final-exam/submit`,
        payload,
        { headers: authHeaders() }
      );
      setResult({ ...res.data, timedOut });
      setPhase('result');
    } catch (e) {
      setError(e.response?.data?.detail || tr('Sınav gönderilemedi', 'Could not submit exam'));
      setPhase('error');
    } finally {
      setSubmitting(false);
    }
  }, [attempt, answers, courseId, submitting]); // eslint-disable-line

  useEffect(() => {
    if (phase !== 'taking' || timeLeft <= 0) return;
    const id = setInterval(() => {
      setTimeLeft(t => {
        if (t <= 1) {
          clearInterval(id);
          handleSubmit(true);
          return 0;
        }
        return t - 1;
      });
    }, 1000);
    return () => clearInterval(id);
  }, [phase, timeLeft]); // eslint-disable-line

  // Faz 2: disabled butonu yerine onClick'te toast ile uyari
  const handleStartExam = () => {
    if (!meta?.can_take_exam) {
      toast.warning(
        lang === 'tr'
          ? 'Sınava girebilmek için kursu tamamlamalısın'
          : 'Complete the course before taking the exam'
      );
      return;
    }
    if (meta.max_attempts > 0 && meta.attempts_left !== null && meta.attempts_left <= 0 && !meta.passed_before) {
      toast.warning(
        lang === 'tr' ? 'Deneme hakkin kalmadi' : 'No attempts left'
      );
      return;
    }
    if (materialsStatus && !materialsStatus.all_completed) {
      const missingCount = materialsStatus.missing_lessons?.length || 0;
      toast.warning(
        lang === 'tr'
          ? `Sınava girmeden önce ${missingCount} zorunlu dersi tamamlamalısın`
          : `You must complete ${missingCount} required lessons before the exam`
      );
      return;
    }
    // Dijital Rozet riza adimi — POST start CAGRILMADAN once gosterilir,
    // cunku start aninda sunucuda started_at yazilir ve timeout oradan
    // hesaplanir. Riza ekraninda gecen sure sinav suresinden dusmemeli.
    // "Devam Et" (yarim kalmis attempt'e donus) akisinda riza adimi hic
    // gosterilmez — sinavin ortasinda alakasiz bir ekran cikmasin.
    if (meta?.dr_consent_required && !meta?.has_in_progress_attempt && !sessionStorage.getItem('drConsentDismissed_' + courseId)) {
      drConsentResolvedRef.current = false;
      setPhase('drConsent');
      return;
    }
    startExam();
  };

  // "Simdi degil" ve Esc icin ortak yol — sunucuya HICBIR yazma yapmaz,
  // sadece bu oturumda tekrar sorulmamasi icin sessionStorage'a isaret koyar.
  // drConsentResolvedRef: hizli Esc+tik ile onay/vazgecme yollarindan
  // startExam()'in iki kez tetiklenmesini engeller.
  const handleDeclineDrConsent = useCallback(() => {
    if (drConsentResolvedRef.current) return;
    drConsentResolvedRef.current = true;
    sessionStorage.setItem('drConsentDismissed_' + courseId, '1');
    startExam();
  }, [courseId]); // eslint-disable-line

  // "Onaliyorum" — PUT basarisiz olsa bile ogrenci sinavda kilitlenmez,
  // hata toast'i gosterilir ve yine startExam() cagrilir.
  const handleConfirmDrConsent = async () => {
    if (!drAgreed || drSaving) return;
    if (drConsentResolvedRef.current) return;
    drConsentResolvedRef.current = true;
    setDrSaving(true);
    try {
      await axios.put(
        `${API}/users/me/dijital-rozet-consent`,
        { consent: true },
        { headers: authHeaders() }
      );
      // Basarili onay sonrasi ayni oturumda (or. "Tekrar Dene" ile intro'ya
      // donus) riza adimi tekrar sorulmasin — hem local meta hem sessionStorage
      // isaretlenir.
      setMeta(m => (m ? { ...m, dr_consent_required: false } : m));
      sessionStorage.setItem('drConsentDismissed_' + courseId, '1');
    } catch (err) {
      toast.error(getApiErrorMessage(err, lang) || t('profile.drConsent.saveError'));
    } finally {
      setDrSaving(false);
    }
    startExam();
  };

  useEffect(() => {
    if (phase !== 'drConsent') return;
    drConsentTitleRef.current?.focus();
    const onKeyDown = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        handleDeclineDrConsent();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [phase]); // eslint-disable-line

  const startExam = async () => {
    try {
      const res = await axios.post(
        `${API}/courses/${courseId}/final-exam/start?lang=${lang}`,
        {},
        { headers: authHeaders() }
      );
      setAttempt(res.data);
      setAnswers({});
      if (res.data.time_limit_minutes > 0) {
        setTimeLeft(res.data.time_limit_minutes * 60);
      }
      setPhase('taking');
    } catch (e) {
      setError(e.response?.data?.detail || tr('Sınav başlatılamadı', 'Could not start exam'));
      setPhase('error');
    }
  };

  const fmtTime = (s) => {
    const m = Math.floor(s / 60);
    const sec = s % 60;
    return `${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}`;
  };

  if (phase === 'loading') {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader size={48} className="animate-spin text-blue-500" />
      </div>
    );
  }

  if (phase === 'error') {
    return (
      <div className="max-w-2xl mx-auto p-6">
        <div className="bg-red-50 border-2 border-red-200 rounded-xl p-6 text-center" data-testid="exam-error">
          <AlertCircle size={48} className="text-red-500 mx-auto mb-3" />
          <div className="text-red-700 font-bold mb-4">{error}</div>
          <button
            onClick={() => navigate(`/courses/${courseId}`)}
            className="px-4 py-2 bg-gray-200 rounded-lg text-sm font-bold hover:bg-gray-300"
          >
            {tr('Kursa Don', 'Back to Course')}
          </button>
        </div>
      </div>
    );
  }

  if (phase === 'intro') {
    return (
      <div className="max-w-2xl mx-auto p-6">
        <div className="bg-white rounded-2xl p-6 border-2 border-gray-200" data-testid="exam-intro">
          <h1 className="text-2xl font-bold mb-2">{tr('Final Sınavı', 'Final Exam')}</h1>
          <p className="text-gray-600 mb-4">{meta.course_title}</p>

          <div className="grid grid-cols-2 gap-3 mb-5">
            <InfoCell label={tr('Soru Sayısı', 'Questions')} value={meta.total_questions} />
            <InfoCell label={tr('Geçer Not', 'Passing Score')} value={`%${meta.passing_score}`} />
            <InfoCell label={tr('Süre', 'Duration')} value={meta.time_limit_minutes > 0 ? `${meta.time_limit_minutes} dk` : tr('Sınırsız', 'Unlimited')} />
            <InfoCell label={tr('Deneme Hakkı', 'Attempts')} value={meta.max_attempts > 0 ? `${meta.attempts_used}/${meta.max_attempts}` : tr('Sınırsız', 'Unlimited')} />
          </div>

          {!meta.can_take_exam && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4 mb-4 text-sm text-yellow-800" data-testid="exam-locked">
              {meta.progress >= 100 && meta.exam_date ? (
                <>
                  {tr('Sınavınız ', 'Your exam opens on ')}<strong>{meta.exam_date}</strong>{tr(' tarihinde açılacak.', '.')}
                  {meta.days_left > 0 ? ' ' + tr(`Kalan gün: ${meta.days_left}`, `Days left: ${meta.days_left}`) : ''}
                </>
              ) : (
                <>
                  {tr('Sınava girmek için kursun tüm derslerini tamamlamalısınız.', 'You must complete all lessons before taking the exam.')}
                  {' '}{tr('Mevcut ilerleme', 'Current progress')}: %{meta.progress}
                </>
              )}
            </div>
          )}

          {meta.passed_before && (
            <div className="bg-green-50 border border-green-200 rounded-xl p-4 mb-4 text-sm text-green-800" data-testid="exam-already-passed">
              {tr('Bu sınavı daha önce geçtiniz. Sertifikanız hesabınızda.', 'You have already passed. Your certificate is in your account.')}
            </div>
          )}

          {meta.max_attempts > 0 && meta.attempts_left !== null && meta.attempts_left <= 0 && !meta.passed_before && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-4 text-sm text-red-800" data-testid="exam-no-attempts">
              {tr('Maksimum deneme hakkınız doldu.', 'No attempts left.')}
            </div>
          )}

          {/* Modul 3.6: Zorunlu materyal eksikligi uyarisi */}
          {materialsStatus && !materialsStatus.all_completed && (
            <div className="bg-amber-50 border-2 border-amber-300 rounded-2xl p-5 mb-4" data-testid="missing-materials-warning">
              <div className="flex items-center gap-2 mb-3">
                <AlertTriangle size={22} className="text-amber-600" />
                <h3 className="font-bold text-lg text-amber-800">
                  {tr('Zorunlu Materyaller Eksik', 'Required Materials Incomplete')}
                </h3>
              </div>
              <p className="text-sm text-amber-800 mb-3">
                {tr(
                  `Sınava girmeden önce ${materialsStatus.missing_lessons.length} zorunlu dersi tamamlamalısınız:`,
                  `You must complete ${materialsStatus.missing_lessons.length} required lessons before the exam:`
                )}
              </p>
              <ul className="list-disc list-inside space-y-1 text-sm text-amber-900">
                {materialsStatus.missing_lessons.map(l => (
                  <li key={l.lesson_id}>{lang === 'tr' ? (l.title_tr || l.title) : (l.title || l.title_tr)}</li>
                ))}
              </ul>
              <div className="mt-3 text-xs text-amber-700">
                {tr(
                  `İlerleme: ${materialsStatus.required_completed} / ${materialsStatus.required_total}`,
                  `Progress: ${materialsStatus.required_completed} / ${materialsStatus.required_total}`
                )}
              </div>
            </div>
          )}

          <div className="flex gap-3">
            <button
              onClick={() => navigate(`/courses/${courseId}`)}
              className="flex-1 px-4 py-3 bg-gray-100 rounded-lg font-bold text-sm hover:bg-gray-200"
            >
              {tr('Geri Dön', 'Back')}
            </button>
            <button
              onClick={handleStartExam}
              className="flex-1 px-4 py-3 bg-blue-500 text-white rounded-lg font-bold text-sm hover:bg-blue-600"
              data-testid="start-exam-btn"
            >
              {meta.has_in_progress_attempt ? tr('Devam Et', 'Continue') : tr('Sınava Başla', 'Start Exam')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (phase === 'drConsent') {
    return (
      <div className="max-w-2xl mx-auto p-6">
        <div
          className="card-clay p-6"
          role="region"
          aria-labelledby="dr-consent-exam-title"
          data-testid="exam-dr-consent-step"
        >
          <BadgeCheck size={40} className="text-[var(--brand-primary)] mb-3" />
          <h1
            id="dr-consent-exam-title"
            ref={drConsentTitleRef}
            tabIndex={-1}
            className="text-2xl font-bold mb-2 text-[var(--text-main)] outline-none"
          >
            {t('exam.drConsent.title')}
          </h1>
          <p className="text-[var(--text-muted)] mb-4">{t('exam.drConsent.intro')}</p>

          <div className="border-t border-[var(--border-light)] pt-4 mb-1">
            <p className="text-xs text-[var(--text-muted)] mb-3">{t('profile.drConsent.description')}</p>

            <ul className="space-y-1.5 mb-4">
              {DR_DATA_ITEMS.map(({ icon: Icon, key }) => (
                <li key={key} className="flex items-center gap-2 text-xs font-semibold text-[var(--text-main)]">
                  <Icon size={14} className="text-[var(--brand-primary)] shrink-0" />
                  {t(key)}
                </li>
              ))}
            </ul>

            <label
              htmlFor="dr-consent-checkbox"
              className="flex items-start gap-3 py-3 min-h-11 cursor-pointer"
            >
              <Checkbox
                id="dr-consent-checkbox"
                checked={drAgreed}
                onCheckedChange={(v) => setDrAgreed(!!v)}
                disabled={drSaving}
                aria-describedby="dr-consent-note"
                data-testid="exam-dr-consent-checkbox"
                className="mt-0.5"
              />
              <span className="font-semibold text-sm text-[var(--text-main)]">
                {t('profile.drConsent.toggleLabel')}
              </span>
            </label>

            <p id="dr-consent-note" className="text-[11px] text-[var(--text-muted)] mb-4 flex items-start gap-1.5">
              <Info size={12} className="shrink-0 mt-0.5" />
              {t('profile.drConsent.note')}
            </p>
          </div>

          <div className="flex gap-3">
            <button
              type="button"
              onClick={handleDeclineDrConsent}
              disabled={drSaving}
              className="flex-1 px-4 py-3 bg-gray-100 rounded-lg font-bold text-sm hover:bg-gray-200 disabled:opacity-50"
              data-testid="exam-dr-consent-decline"
            >
              {t('exam.drConsent.declineButton')}
            </button>
            <button
              type="button"
              onClick={handleConfirmDrConsent}
              disabled={!drAgreed || drSaving}
              aria-disabled={!drAgreed || drSaving}
              className="flex-1 px-4 py-3 bg-[var(--brand-primary)] text-white rounded-lg font-bold text-sm hover:opacity-90 disabled:opacity-50"
              data-testid="exam-dr-consent-confirm"
            >
              {drSaving ? '...' : t('exam.drConsent.confirmButton')}
            </button>
          </div>

          <p className="text-[11px] text-[var(--text-muted)] mt-3 text-center">
            {t('exam.drConsent.declineHint')}
          </p>
        </div>
      </div>
    );
  }

  if (phase === 'taking' && attempt) {
    const answeredCount = Object.keys(answers).length;
    return (
      <div className="max-w-3xl mx-auto p-6">
        <div className="sticky top-0 bg-white border-b-2 border-gray-200 p-3 mb-4 flex items-center justify-between z-10" data-testid="exam-header">
          <div className="text-sm">
            {tr('Cevaplanan', 'Answered')}: <strong>{answeredCount}/{attempt.questions.length}</strong>
          </div>
          {attempt.time_limit_minutes > 0 && (
            <div className={`flex items-center gap-2 font-bold ${timeLeft < 60 ? 'text-red-600' : 'text-gray-800'}`} data-testid="exam-timer">
              <Clock size={18} />
              {fmtTime(timeLeft)}
            </div>
          )}
        </div>

        {attempt.questions.map((q, idx) => (
          <div key={q.question_id} className="bg-white rounded-xl p-5 mb-3 border-2 border-gray-200" data-testid={`question-${idx}`}>
            <div className="font-bold mb-3">
              {idx + 1}. {q.question}
              <span className="ml-2 text-xs text-gray-500">({q.points} {tr('puan', 'pt')})</span>
            </div>
            <div className="space-y-2">
              {q.options.map((opt, oi) => (
                <label
                  key={oi}
                  className={`flex items-center gap-3 p-3 border-2 rounded-lg cursor-pointer transition ${
                    answers[q.question_id] === opt ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <input
                    type="radio"
                    name={q.question_id}
                    value={opt}
                    checked={answers[q.question_id] === opt}
                    onChange={() => setAnswers(a => ({ ...a, [q.question_id]: opt }))}
                    data-testid={`q${idx}-option-${oi}`}
                  />
                  <span className="text-sm">{opt}</span>
                </label>
              ))}
            </div>
          </div>
        ))}

        <button
          onClick={() => handleSubmit(false)}
          disabled={submitting}
          className="w-full px-6 py-4 bg-green-500 text-white rounded-xl font-bold text-lg hover:bg-green-600 disabled:opacity-50 flex items-center justify-center gap-2"
          data-testid="submit-exam-btn"
        >
          {submitting && <Loader size={20} className="animate-spin" />}
          {tr('Sınavı Bitir', 'Submit Exam')}
        </button>
      </div>
    );
  }

  if (phase === 'result' && result) {
    return (
      <div className="max-w-2xl mx-auto p-6">
        <div className={`rounded-2xl p-8 text-center ${result.passed ? 'bg-green-50 border-2 border-green-200' : 'bg-red-50 border-2 border-red-200'}`} data-testid="exam-result">
          {result.passed ? (
            <>
              <CheckCircle size={64} className="text-green-500 mx-auto mb-4" />
              <h1 className="text-3xl font-bold text-green-700 mb-2">{tr('Tebrikler!', 'Congratulations!')}</h1>
              <p className="text-green-700">{tr('Sınavı başarıyla geçtiniz.', 'You passed the exam.')}</p>
            </>
          ) : (
            <>
              <XCircle size={64} className="text-red-500 mx-auto mb-4" />
              <h1 className="text-3xl font-bold text-red-700 mb-2">{tr('Başarısız', 'Failed')}</h1>
              <p className="text-red-700">
                {result.timedOut
                  ? tr('Süre doldu.', 'Time expired.')
                  : tr('Geçer nota ulaşamadınız.', 'Did not reach passing score.')}
              </p>
            </>
          )}
          <div className="mt-6 text-5xl font-bold">%{result.score}</div>
          <div className="text-sm text-gray-600 mt-1">
            {result.earned_points}/{result.total_points} {tr('puan', 'points')}
            {' · '}
            {tr('Geçer Not', 'Passing')}: %{result.passing_score}
          </div>

          {result.certificate && (
            <div className="mt-6 bg-white rounded-xl p-5 border-2 border-yellow-300" data-testid="certificate-card">
              <Award size={48} className="text-yellow-500 mx-auto mb-2" />
              <div className="font-bold text-lg mb-1">{tr('Sertifikanız Hazır', 'Certificate Ready')}</div>
              <div className="text-sm text-gray-600 mb-3">
                {tr('Doğrulama Kodu', 'Verification')}: <strong>{result.certificate.verification_code}</strong>
              </div>
              <button
                onClick={() => downloadExamCertPdf(result.certificate.certificate_id, meta?.course_title_tr || meta?.course_title)}
                className="inline-block px-5 py-2 bg-yellow-500 text-white rounded-lg font-bold text-sm hover:bg-yellow-600 cursor-pointer"
                data-testid="download-cert-btn"
              >
                {tr('Sertifikayı İndir (PDF)', 'Download Certificate (PDF)')}
              </button>
            </div>
          )}

          <div className="mt-6 flex gap-3 justify-center">
            <button
              onClick={() => navigate('/dashboard')}
              className="px-4 py-2 bg-gray-200 rounded-lg text-sm font-bold hover:bg-gray-300"
            >
              {tr('Ana Sayfa', 'Home')}
            </button>
            {!result.passed && (
              <button
                onClick={() => { setPhase('intro'); setAttempt(null); setResult(null); }}
                className="px-4 py-2 bg-blue-500 text-white rounded-lg text-sm font-bold hover:bg-blue-600"
                data-testid="retry-btn"
              >
                {tr('Tekrar Dene', 'Try Again')}
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  return null;
}

function InfoCell({ label, value }) {
  return (
    <div className="bg-gray-50 rounded-lg p-3">
      <div className="text-xs text-gray-500 mb-1">{label}</div>
      <div className="font-bold">{value}</div>
    </div>
  );
}
